import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  ConfigService,
  GetFileInputData,
  MessageService,
  OfferBundleStatus,
  ResourceService,
} from "common";
import { catchError, map } from "rxjs/operators";
import { defaultObservableErrorHandler } from "@partner/shared/Tools";
import { DownloadRequirementDocumentData, DeleteRequirementDocumentData, SaveRequirementDocumentData, SubmissionQueryParams, SubmissionsHintsQueryParams, SubmissionsHintsResponse, SubmissionsQueryResult } from "../domain/models/submissions.model";
import { ApiPaths } from "../../../core/const/api-paths";
import { AccountData, ApplicationData, ApplicationDocumentsQueryResult, ContactData, ContactQueryResult, ContactsQueryParams, SaveAccountData, SaveApplicationData, SaveContactData } from "../domain/models/application.model";
import { LoanData } from "../domain/models/loan.model";
import { adaptApplicationData, ApplicationResponse } from "./api-models/application-api.model";

@Injectable({
  providedIn: "root",
})
export class SubmissionsRemote {
  constructor(
    private readonly http: HttpClient,
    private readonly messageService: MessageService,
    private readonly resourceService: ResourceService,
    private readonly configService: ConfigService,
  ) { }

  getSubmissions(params: SubmissionQueryParams): Observable<SubmissionsQueryResult> {
    return this.http.get<SubmissionsQueryResult>(ApiPaths.submission.list, { params: <any>params });
  }

  getSubmissionsHints(params: SubmissionsHintsQueryParams): Observable<string[]> {
    return this.http.get<SubmissionsHintsResponse>(ApiPaths.submission.hints, { params: <any>params })
      .pipe(map(response => (response.values)));
  }

  getApplicationData(id: number): Observable<ApplicationData> {
    return this.http
      .get<ApplicationResponse>(ApiPaths.submission.application(id), {
        headers: this.configService.environment.cache.noCache,
      })
      .pipe(
        catchError(defaultObservableErrorHandler(this.messageService)),
        map(response => adaptApplicationData(response))
      );
  }

  getAccountData(id: number): Observable<AccountData> {
    return this.http
      .get<AccountData>(ApiPaths.submission.applicationAccount(id))
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  getApplicationDocuments(id: number): Observable<ApplicationDocumentsQueryResult> {
    return this.http
      .get<ApplicationDocumentsQueryResult>(ApiPaths.submission.applicationDocuments(id))
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  getContactsData(params: ContactsQueryParams): Observable<ContactQueryResult> {
    return this.http
      .get<ContactQueryResult>(ApiPaths.submission.contacts, { params: <any>params })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  downloadApplicationDocument(applicationId: number, documentId: number, filename: string, hideLoader?: boolean): Observable<void> {
    const inputData: GetFileInputData = {
      url: ApiPaths.submission.applicationDocument(applicationId, documentId),
      filename,
      hideLoader
    };
    return this.resourceService.download(inputData);
  }

  getLoanData(id: number): Observable<LoanData> {
    return this.http.get<LoanData>(ApiPaths.submission.submissionLoan(id))
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  sendCheckout(appId: number): Observable<void> {
    return this.http.post<void>(ApiPaths.submission.applicationCreateLoans(appId), {})
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  resendCheckout(appId: number): Observable<void> {
    return this.http.post<void>(ApiPaths.submission.applicationResendCheckout(appId), {})
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  sendBankLink(appId: number, email: string): Observable<void> {
    return this.http.post<void>(ApiPaths.submission.bankLinkingInvitation(appId), { email })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  removeCheckoutRequirementDocument(data: DeleteRequirementDocumentData): Observable<void> {
    return this.http
      .delete<void>(ApiPaths.submission.checkoutRequirementFile(data.requirementId, data.documentId))
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  downloadCheckoutRequirementDocument(data: DownloadRequirementDocumentData): void {
    const inputData: GetFileInputData = {
      url: ApiPaths.submission.checkoutRequirementFile(data.requirementId, data.documentId),
      filename: data.filename
    };
    this.resourceService.download(inputData).subscribe();
  }

  saveCheckoutRequirementDocument(data: SaveRequirementDocumentData): Observable<void> {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("type", data.type);
    formData.append("file", data.data);
    return this.http
      .post<void>(ApiPaths.submission.checkoutRequirementFileCategory(data.requirementId, data.category), formData)
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  updateOfferBundleStatus(offerBundleId: number, status: OfferBundleStatus) {
    return this.http.patch(ApiPaths.submission.offerBundleStatus(offerBundleId), { status })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  createAccountData(data: SaveAccountData, hideLoader?: boolean): Observable<AccountData> {
    return this.http.post<AccountData>(ApiPaths.submission.newApplicationAccount,
      { ...data, active: true }, { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  updateAccountData(accountId: number, data: SaveAccountData, hideLoader?: boolean): Observable<void> {
    return this.http.patch<void>(ApiPaths.submission.applicationAccount(accountId),
      { ...data, active: true }, { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  createApplicationData(data: SaveApplicationData, hideLoader?: boolean): Observable<Partial<ApplicationData>> {
    return this.http.post(ApiPaths.submission.newApplication, data, { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  updateApplicationData(applicationId: number, data: SaveApplicationData, hideLoader?: boolean): Observable<Partial<ApplicationData>> {
    return this.http.patch(ApiPaths.submission.application(applicationId), data, { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  createContactData(data: SaveContactData, hideLoader?: boolean): Observable<ContactData> {
    const payload = {
      ...data,
      birthdate: data.birthdate ? new Date(data.birthdate).toISOString() : undefined
    }
    return this.http.post<ContactData>(ApiPaths.submission.newContact, payload, { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  updateContactData(contactId: number, data: SaveContactData, hideLoader?: boolean): Observable<ContactData> {
    const payload = {
      ...data,
      birthdate: data.birthdate ? new Date(data.birthdate).toISOString() : undefined,
    }
    return this.http.patch<ContactData>(ApiPaths.submission.contact(contactId), payload, { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  removeContactData(contactId: number, hideLoader?: boolean): Observable<ContactData> {
    return this.http.delete<ContactData>(ApiPaths.submission.contact(contactId), { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  saveApplicationDocument(applicationId: number, data: SaveRequirementDocumentData, hideLoader?: boolean): Observable<void> {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("type", data.type);
    formData.append("file", data.data);
    return this.http
      .post<void>(ApiPaths.submission.applicationDocumentCategory(applicationId, data.category), formData, { headers: { hideLoader: hideLoader ? "true" : "" } })
      .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }

  deleteApplicationDocument(applicationId: number, documentId: number, hideLoader?: boolean): Observable<void> {
    return this.http.delete<void>(ApiPaths.submission.applicationDocumentPartner(applicationId, documentId), { headers: { hideLoader: hideLoader ? "true" : "" } })
    .pipe(catchError(defaultObservableErrorHandler(this.messageService)));
  }
}
